import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  LoadingState,
  Metric,
  Select,
  TextArea,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type {
  ComplianceFramework,
  FrameworkLicense,
  FrameworkRequirement,
  FrameworkVersion,
  FrameworkVersionComparison,
  GapStatus,
  ImportRequirementRow,
  RequirementCoverage,
  RequirementDelta,
} from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync, type AsyncState } from "../../lib/useAsync";
import { frameworksApi } from "../../lib/api";
import { GOVERNANCE_PERMISSIONS, GovPanel, GovernedAction } from "../../governance";
import {
  ContentKindBadge,
  ContentPolicyBadge,
  CoverageBadge,
  ScopeBadge,
  VersionStatusBadge,
  contentPolicyNote,
} from "./labels";

interface VersionContext {
  framework: ComplianceFramework;
  version: FrameworkVersion;
  siblings: FrameworkVersion[];
}

type RequirementRow = FrameworkRequirement & { coverage: RequirementCoverage | undefined };
type GapRow = RequirementCoverage & { id: string };
type LicenseRow = FrameworkLicense & { live: boolean };
type DeltaRow = RequirementDelta & { id: string };

const KIND_INDENT: Record<FrameworkRequirement["kind"], number> = {
  family: 0,
  control: 1,
  requirement: 1,
  criterion: 1,
};

// Requirement hierarchy, coverage, gaps, comparison and licences for one
// framework version. Coverage status, gap membership and version deltas are all
// read from the backend — the browser never counts mappings itself.
export function GovFrameworkVersion() {
  const { versionId = "" } = useParams();
  const [params] = useSearchParams();
  const frameworkId = params.get("frameworkId");
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [gapStatus, setGapStatus] = useState<GapStatus>("uncovered");
  const [contentFor, setContentFor] = useState<FrameworkRequirement | null>(null);

  // There is no get-version-by-id route in the frozen contract, so the version
  // is resolved through its framework: one call when the link carried the
  // framework id, otherwise a scan of the frameworks the tenant can see.
  const context = useAsync<VersionContext | null>(async () => {
    const candidates = frameworkId
      ? [(await frameworksApi.framework(orgId, frameworkId)).data.framework]
      : (await frameworksApi.frameworks(orgId)).data.frameworks;
    for (const framework of candidates) {
      const versions = (await frameworksApi.versions(orgId, framework.id)).data.versions;
      const version = versions.find((v) => v.id === versionId);
      if (version) return { framework, version, siblings: versions };
    }
    return null;
  }, [orgId, versionId, frameworkId]);

  const requirements = useAsync(async () => {
    const [reqs, coverage] = await Promise.all([
      frameworksApi.requirements(orgId, versionId, query || undefined),
      frameworksApi.coverage(orgId, versionId),
    ]);
    return { requirements: reqs.data.requirements, coverage: coverage.data.coverage };
  }, [orgId, versionId, query]);

  const gaps = useAsync(
    async () => (await frameworksApi.gaps(orgId, versionId, gapStatus)).data,
    [orgId, versionId, gapStatus],
  );

  const licenses = useAsync(
    async () => (await frameworksApi.licenses(orgId, versionId)).data.licenses,
    [orgId, versionId],
  );

  const reloadAll = () => {
    context.reload();
    requirements.reload();
    gaps.reload();
  };

  const version = context.data?.version;
  const framework = context.data?.framework;

  return (
    <>
      <div className="page-head">
        <div>
          <p className="muted-note">
            <Link to="/studio/governance/frameworks">← Frameworks</Link>
          </p>
          <h1>{framework ? framework.name : "Framework version"}</h1>
          {framework && version ? (
            <div className="badge-row">
              <strong>{version.versionLabel}</strong>
              <VersionStatusBadge status={version.status} />
              <ScopeBadge scope={framework.scope} />
              <ContentPolicyBadge policy={framework.contentPolicy} />
            </div>
          ) : null}
        </div>
        {version && framework ? (
          <div className="badge-row">
            {version.status === "draft" && (
              <>
                <ImportRequirements
                  orgId={orgId}
                  versionId={version.id}
                  licenses={licenses.data ?? []}
                  onDone={reloadAll}
                />
                <GovernedAction
                  permission={GOVERNANCE_PERMISSIONS.frameworksManage}
                  label="Activate version"
                  title={`Activate ${version.versionLabel}`}
                  description="The framework's current active version is superseded. Mappings against it are carried forward by the backend."
                  consequence="Activation cannot be undone, and requirements can no longer be imported into this version."
                  confirmLabel="Activate"
                  busyLabel="Activating…"
                  triggerVariant="primary"
                  triggerSize="md"
                  onConfirm={() => frameworksApi.activateVersion(orgId, version.id)}
                  onDone={reloadAll}
                />
              </>
            )}
          </div>
        ) : null}
      </div>

      {context.loading && <LoadingState label="Loading framework version…" />}
      {!context.loading && context.data === null && !context.error && (
        <EmptyState
          title="Version not found"
          description="This framework version is not visible to your organization."
        />
      )}
      {context.error && <GovPanel state={context} bare>{() => null}</GovPanel>}

      {version && framework && (
        <>
          <div className="metric-grid">
            <div className="metric-card">
              <Metric value={version.requirementCount} label="Requirements" />
            </div>
            <div className="metric-card">
              <Metric
                value={requirements.data ? requirements.data.coverage.covered : 0}
                label="Covered"
              />
            </div>
            <div className="metric-card">
              <Metric
                value={requirements.data ? requirements.data.coverage.weak : 0}
                label="Weakly covered"
                tone="var(--ods-color-warning)"
              />
            </div>
            <div className="metric-card">
              <Metric
                value={requirements.data ? requirements.data.coverage.uncovered : 0}
                label="Uncovered"
                tone="var(--ods-color-danger)"
              />
            </div>
          </div>
          <p className="muted-note">
            Coverage is measured by the Policy AI backend. Only mappings a person has verified count towards it;
            unverified AI suggestions never do. {contentPolicyNote(framework.contentPolicy)}
          </p>

          <GovPanel
            title="Requirements"
            state={requirements}
            loadingLabel="Loading requirements…"
            isEmpty={(d) => d.requirements.length === 0}
            emptyTitle={query ? "No requirements match that search" : "No requirements yet"}
            emptyDescription={
              query
                ? "Try a different requirement key or title."
                : "Import a requirement tree into this draft version to start mapping."
            }
            action={
              <form
                className="filter-row"
                onSubmit={(e) => {
                  e.preventDefault();
                  setQuery(search.trim());
                }}
              >
                <Field label="Search requirements">
                  {(id) => (
                    <TextInput
                      id={id}
                      value={search}
                      placeholder="e.g. CC6.1 or access"
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  )}
                </Field>
                <Button type="submit" variant="secondary" size="sm">
                  Search
                </Button>
              </form>
            }
          >
            {(data) => (
              <DataTable
                columns={[
                  {
                    key: "requirementKey",
                    header: "Requirement",
                    render: (r: RequirementRow) => (
                      <div style={{ paddingLeft: `${KIND_INDENT[r.kind] * 16}px` }}>
                        <strong>{r.requirementKey}</strong> {r.title}
                        {r.summary ? <div className="muted-note">{r.summary}</div> : null}
                      </div>
                    ),
                  },
                  { key: "kind", header: "Kind", render: (r: RequirementRow) => <Badge>{r.kind}</Badge> },
                  {
                    key: "coverage",
                    header: "Coverage",
                    render: (r: RequirementRow) =>
                      r.coverage ? <CoverageBadge status={r.coverage.status} /> : "Not assessed",
                  },
                  {
                    key: "mappings",
                    header: "Mappings",
                    render: (r: RequirementRow) =>
                      r.coverage ? (
                        <span className="muted-note">
                          {r.coverage.verifiedCount} verified · {r.coverage.proposedCount} proposed
                        </span>
                      ) : (
                        "None"
                      ),
                  },
                  {
                    key: "actions",
                    header: "",
                    render: (r: RequirementRow) => (
                      <div className="badge-row">
                        <Button variant="ghost" size="sm" onClick={() => setContentFor(r)}>
                          Content
                        </Button>
                        <Link
                          className="ods-btn ods-btn--ghost ods-btn--sm"
                          to={`/studio/governance/mappings?requirementId=${r.id}&frameworkVersionId=${version.id}`}
                        >
                          Mappings
                        </Link>
                      </div>
                    ),
                  },
                ]}
                rows={data.requirements.map(
                  (r): RequirementRow => ({
                    ...r,
                    coverage: data.coverage.requirements.find((c) => c.requirementId === r.id),
                  }),
                )}
                caption="Requirements in this framework version"
              />
            )}
          </GovPanel>

          <GovPanel
            title="Gaps"
            state={gaps}
            loadingLabel="Loading gaps…"
            isEmpty={(d) => d.requirements.length === 0}
            emptyTitle="No requirements in this state"
            emptyDescription="Every requirement in this version is outside the selected gap status."
            action={
              <Field label="Gap status">
                {(id) => (
                  <Select
                    id={id}
                    value={gapStatus}
                    onChange={(e) => setGapStatus(e.target.value as GapStatus)}
                  >
                    <option value="uncovered">Uncovered</option>
                    <option value="weak">Weakly covered</option>
                    <option value="stale">Stale</option>
                  </Select>
                )}
              </Field>
            }
          >
            {(data) => (
              <DataTable
                columns={[
                  {
                    key: "requirementKey",
                    header: "Requirement",
                    render: (r: GapRow) => (
                      <>
                        <strong>{r.requirementKey}</strong> {r.title}
                      </>
                    ),
                  },
                  { key: "status", header: "Status", render: (r: GapRow) => <CoverageBadge status={r.status} /> },
                  {
                    key: "counts",
                    header: "Mappings",
                    render: (r: GapRow) => (
                      <span className="muted-note">
                        {r.verifiedCount} verified · {r.proposedCount} proposed · {r.staleCount} stale
                      </span>
                    ),
                  },
                  {
                    key: "actions",
                    header: "",
                    render: (r: GapRow) => (
                      <Link
                        className="ods-btn ods-btn--ghost ods-btn--sm"
                        to={`/studio/governance/mappings?requirementId=${r.requirementId}&frameworkVersionId=${version.id}`}
                      >
                        Map a policy
                      </Link>
                    ),
                  },
                ]}
                rows={data.requirements.map((r): GapRow => ({ ...r, id: r.requirementId }))}
                caption={`Requirements with status ${gaps.data?.status ?? gapStatus}`}
              />
            )}
          </GovPanel>

          <CompareVersions orgId={orgId} version={version} siblings={context.data?.siblings ?? []} />

          <Licenses
            orgId={orgId}
            version={version}
            framework={framework}
            state={licenses}
            onDone={() => {
              licenses.reload();
              requirements.reload();
            }}
          />
        </>
      )}

      {contentFor ? (
        <RequirementContentDialog
          orgId={orgId}
          requirement={contentFor}
          onClose={() => setContentFor(null)}
        />
      ) : null}
    </>
  );
}

function RequirementContentDialog({
  orgId,
  requirement,
  onClose,
}: {
  orgId: string;
  requirement: FrameworkRequirement;
  onClose: () => void;
}) {
  const state = useAsync(
    async () => (await frameworksApi.requirementContent(orgId, requirement.id)).data,
    [orgId, requirement.id],
  );

  return (
    <Dialog
      open
      title={`${requirement.requirementKey} content`}
      description={requirement.title}
      onClose={onClose}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <GovPanel
        state={state}
        bare
        loadingLabel="Loading requirement content…"
        isEmpty={(d) => d.content.length === 0 && d.withheld.length === 0}
        emptyTitle="No stored content"
        emptyDescription="Odingard stores identifiers and hierarchy for this requirement. No text is held."
      >
        {(data) => (
          <div className="stack">
            {data.content.map((c) => (
              <div key={c.id}>
                <ContentKindBadge kind={c.contentKind} />
                <p style={{ whiteSpace: "pre-wrap" }}>{c.body}</p>
                <p className="muted-note">
                  <code>{c.contentHash}</code>
                </p>
              </div>
            ))}
            {data.withheld.map((w) => (
              <div key={w.id} className="ods-denial" role="note">
                <strong>Content withheld.</strong> {w.reason}
              </div>
            ))}
          </div>
        )}
      </GovPanel>
    </Dialog>
  );
}

function CompareVersions({
  orgId,
  version,
  siblings,
}: {
  orgId: string;
  version: FrameworkVersion;
  siblings: FrameworkVersion[];
}) {
  const others = siblings.filter((v) => v.id !== version.id);
  const [target, setTarget] = useState("");
  const [comparison, setComparison] = useState<FrameworkVersionComparison | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const compare = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await frameworksApi.compareVersions(orgId, version.id, target);
      setComparison(res.data.comparison);
    } catch (err: unknown) {
      setComparison(null);
      setError(err instanceof Error ? err.message : "Comparison failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Compare versions" />
      <CardBody>
        {others.length === 0 ? (
          <EmptyState
            title="Nothing to compare"
            description="This framework has only one version so far."
          />
        ) : (
          <>
            <div className="filter-row">
              <Field label="Compare with">
                {(id) => (
                  <Select id={id} value={target} onChange={(e) => setTarget(e.target.value)}>
                    <option value="">Select a version…</option>
                    {others.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.versionLabel} ({v.status})
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Button variant="secondary" disabled={!target || busy} onClick={() => void compare()}>
                {busy ? "Comparing…" : "Compare"}
              </Button>
            </div>
            {error ? (
              <div className="ods-denial" role="alert">
                {error}
              </div>
            ) : null}
            {comparison ? (
              <>
                <p className="muted-note">
                  {comparison.added} added · {comparison.removed} removed · {comparison.changed} changed ·{" "}
                  {comparison.unchanged} unchanged. Deltas are computed by the backend.
                </p>
                <DataTable
                  columns={[
                    {
                      key: "requirementKey",
                      header: "Requirement",
                      render: (d: DeltaRow) => <strong>{d.requirementKey}</strong>,
                    },
                    {
                      key: "change",
                      header: "Change",
                      render: (d: DeltaRow) => <Badge>{d.change}</Badge>,
                    },
                    {
                      key: "titles",
                      header: "Title",
                      render: (d: DeltaRow) =>
                        d.fromTitle === d.toTitle ? (
                          (d.toTitle ?? "Untitled")
                        ) : (
                          <>
                            <span className="muted-note">{d.fromTitle ?? "Untitled"}</span> → {d.toTitle ?? "Untitled"}
                          </>
                        ),
                    },
                    {
                      key: "carried",
                      header: "Mappings carried",
                      render: (d: DeltaRow) => d.carriedMappingCount,
                    },
                  ]}
                  rows={comparison.deltas.map((d): DeltaRow => ({ ...d, id: d.requirementKey }))}
                  caption="Requirement deltas between versions"
                />
              </>
            ) : null}
          </>
        )}
      </CardBody>
    </Card>
  );
}

function Licenses({
  orgId,
  version,
  framework,
  state,
  onDone,
}: {
  orgId: string;
  version: FrameworkVersion;
  framework: ComplianceFramework;
  state: AsyncState<FrameworkLicense[]>;
  onDone: () => void;
}) {
  return (
    <GovPanel
      title="Licences"
      state={state}
      loadingLabel="Loading licences…"
      isEmpty={(d) => d.length === 0}
      emptyTitle="No licence recorded"
      emptyDescription={contentPolicyNote(framework.contentPolicy)}
      emptyAction={<RecordLicense orgId={orgId} versionId={version.id} onDone={onDone} />}
      action={<RecordLicense orgId={orgId} versionId={version.id} onDone={onDone} />}
    >
      {(licenses) => (
        <>
          <p className="muted-note">{contentPolicyNote(framework.contentPolicy)}</p>
          <DataTable
            columns={[
              {
                key: "licensor",
                header: "Licensor",
                render: (l: LicenseRow) => (
                  <>
                    <strong>{l.licensor}</strong>
                    <div className="muted-note">
                      {l.licenseType}
                      {l.reference ? ` · ${l.reference}` : ""}
                    </div>
                  </>
                ),
              },
              {
                key: "fullText",
                header: "Full text",
                render: (l: LicenseRow) =>
                  l.permitsFullText ? (
                    <Badge tone="warning">Permits licensed text</Badge>
                  ) : (
                    <Badge tone="neutral">Identifiers only</Badge>
                  ),
              },
              {
                key: "window",
                header: "Window",
                render: (l: LicenseRow) => (
                  <span className="muted-note">
                    {formatDate(l.grantedAt)} → {l.expiresAt ? formatDate(l.expiresAt) : "No end date"}
                  </span>
                ),
              },
              {
                key: "status",
                header: "Status",
                render: (l: LicenseRow) =>
                  l.live ? <Badge tone="success">Live</Badge> : <Badge tone="danger">Revoked</Badge>,
              },
              {
                key: "actions",
                header: "",
                render: (l: LicenseRow) =>
                  l.live ? (
                    <GovernedAction
                      permission={GOVERNANCE_PERMISSIONS.frameworksManage}
                      label="Revoke"
                      title={`Revoke the ${l.licensor} licence`}
                      description="Content that depended on this licence stops being readable immediately."
                      consequence="Any licensed text stored under this licence is withheld from every reader after revocation."
                      confirmLabel="Revoke licence"
                      busyLabel="Revoking…"
                      destructive
                      onConfirm={() => frameworksApi.revokeLicense(orgId, l.id)}
                      onDone={onDone}
                    />
                  ) : null,
              },
            ]}
            rows={licenses.map((l): LicenseRow => ({ ...l, live: l.revokedAt === null }))}
            caption="Framework licences"
          />
        </>
      )}
    </GovPanel>
  );
}

function RecordLicense({
  orgId,
  versionId,
  onDone,
}: {
  orgId: string;
  versionId: string;
  onDone: () => void;
}) {
  const [licensor, setLicensor] = useState("");
  const [licenseType, setLicenseType] = useState("commercial");
  const [reference, setReference] = useState("");
  const [grantedAt, setGrantedAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [permitsFullText, setPermitsFullText] = useState(true);

  return (
    <GovernedAction
      permission={GOVERNANCE_PERMISSIONS.frameworksManage}
      label="Record licence"
      title="Record a tenant licence"
      description="Protected standard text can only be stored or read while a live licence permits it."
      confirmLabel="Record licence"
      busyLabel="Recording…"
      onConfirm={() =>
        frameworksApi.createLicense(orgId, versionId, {
          licensor: licensor.trim(),
          licenseType: licenseType.trim(),
          reference: reference.trim(),
          permitsFullText,
          grantedAt: grantedAt || new Date().toISOString().slice(0, 10),
          ...(expiresAt ? { expiresAt } : {}),
        })
      }
      onDone={() => {
        setLicensor("");
        setReference("");
        onDone();
      }}
    >
      <Field label="Licensor">
        {(id) => <TextInput id={id} value={licensor} onChange={(e) => setLicensor(e.target.value)} />}
      </Field>
      <Field label="Licence type">
        {(id) => <TextInput id={id} value={licenseType} onChange={(e) => setLicenseType(e.target.value)} />}
      </Field>
      <Field label="Reference" help="Contract or order number, if you have one.">
        {(id) => <TextInput id={id} value={reference} onChange={(e) => setReference(e.target.value)} />}
      </Field>
      <Field label="Granted on">
        {(id) => (
          <TextInput id={id} type="date" value={grantedAt} onChange={(e) => setGrantedAt(e.target.value)} />
        )}
      </Field>
      <Field label="Expires on" help="Optional. Content is withheld once a licence lapses.">
        {(id) => (
          <TextInput id={id} type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
        )}
      </Field>
      <Field label="Permits full text">
        {(id) => (
          <Select
            id={id}
            value={permitsFullText ? "yes" : "no"}
            onChange={(e) => setPermitsFullText(e.target.value === "yes")}
          >
            <option value="yes">Yes, licensed text may be stored</option>
            <option value="no">No, identifiers only</option>
          </Select>
        )}
      </Field>
    </GovernedAction>
  );
}

function ImportRequirements({
  orgId,
  versionId,
  licenses,
  onDone,
}: {
  orgId: string;
  versionId: string;
  licenses: FrameworkLicense[];
  onDone: () => void;
}) {
  const [rows, setRows] = useState("");
  const [licenseId, setLicenseId] = useState("");
  const live = licenses.filter((l) => l.revokedAt === null && l.permitsFullText);

  const parse = (): ImportRequirementRow[] => {
    const parsed: unknown = JSON.parse(rows);
    if (!Array.isArray(parsed)) throw new Error("Provide a JSON array of requirements.");
    return parsed as ImportRequirementRow[];
  };

  return (
    <GovernedAction
      permission={GOVERNANCE_PERMISSIONS.frameworksManage}
      label="Import requirements"
      title="Import a requirement tree"
      description="JSON rows are imported into this draft version. Rows carrying licensed text are accepted only while a live licence permits full text."
      confirmLabel="Import"
      busyLabel="Importing…"
      triggerSize="md"
      onConfirm={() =>
        frameworksApi.importRequirements(orgId, versionId, {
          requirements: parse(),
          ...(licenseId ? { licenseId } : {}),
        })
      }
      onDone={() => {
        setRows("");
        onDone();
      }}
    >
      <Field
        label="Requirements (JSON)"
        help='e.g. [{"requirementKey":"AC-2","title":"Account Management","kind":"control"}]'
      >
        {(id) => <TextArea id={id} rows={8} value={rows} onChange={(e) => setRows(e.target.value)} />}
      </Field>
      <Field
        label="Licence for protected text"
        help="Required only when a row carries licensed standard text."
      >
        {(id) => (
          <Select id={id} value={licenseId} onChange={(e) => setLicenseId(e.target.value)}>
            <option value="">None, no licensed text in this import</option>
            {live.map((l) => (
              <option key={l.id} value={l.id}>
                {l.licensor} · {l.licenseType}
              </option>
            ))}
          </Select>
        )}
      </Field>
    </GovernedAction>
  );
}
